// Settings layout with sidebar navigation between settings sections.

import { Outlet, Link, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { SETTINGS_NAV_GROUPS, isSettingsNavActive } from '@/features/settings/logic';

// --- Icon components ---

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}


function SlidersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}


function ContactIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 18a2 2 0 00-2-2H9a2 2 0 00-2 2" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <circle cx="12" cy="10" r="2" />
      <line x1="8" y1="2" x2="8" y2="4" />
      <line x1="16" y1="2" x2="16" y2="4" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function WebhookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 012 17c.01-.7.2-1.4.57-2" />
      <path d="M6 17a4 4 0 004.56-6.31A4 4 0 0115.56 8H20" />
      <path d="M12 6a4 4 0 00-4 4c0 .74.21 1.43.56 2.02" />
      <circle cx="12" cy="6" r="2" />
      <circle cx="6" cy="17" r="2" />
      <circle cx="18" cy="17" r="2" />
    </svg>
  );
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  user: UserIcon,
  contact: ContactIcon,
  sliders: SlidersIcon,
  shield: ShieldIcon,
  monitor: MonitorIcon,
  key: KeyIcon,
  webhook: WebhookIcon,
  mail: MailIcon,
};

export const SettingsLayout = observer(function SettingsLayout() {
  const { t } = useI18n();
  const location = useLocation();

  return (
    <div className="flex flex-col md:flex-row gap-0 md:gap-6 min-h-full">
      {/* Settings sidebar */}
      <aside className="w-full md:w-64 lg:w-72 flex-shrink-0 md:sticky md:top-0 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto scrollbar-slim">
        {/* Back to app link */}
        <Link
          to="/inbox"
          className="flex items-center gap-2 px-3 py-2 mb-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {t('settings.backToApp')}
        </Link>

        <h2 className="px-3 mb-4 text-lg font-semibold text-foreground">{t('settings.title')}</h2>

        <nav className="space-y-6" aria-label={t('settings.navigation')}>
          {SETTINGS_NAV_GROUPS.map((group) => (
            <div key={group.id}>
              <p className="px-3 mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t(group.labelKey)}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isSettingsNavActive(location.pathname, item.href);
                  const Icon = ICON_MAP[item.icon];
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        className={`
                          flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
                          ${active
                            ? 'bg-accent text-accent-foreground font-medium'
                            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                          }
                        `}
                        aria-current={active ? 'page' : undefined}
                      >
                        {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                        <span>{t(item.labelKey)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Divider (desktop) */}
      <div className="hidden md:block w-px bg-border flex-shrink-0" />

      {/* Settings content */}
      <main className="flex-1 min-w-0 py-2 md:py-0">
        <Outlet />
      </main>
    </div>
  );
});
